<script setup lang="ts">
import { twMerge } from 'tailwind-merge'
import type { Article } from '~/models/content'
import { isSizeMobile } from '~/composables/window-events'

interface Props {
  article: Article
  coverPosition?: string
  optimizedCover?: string
  isFullscreen?: boolean
  twitterUrl?: string | null
  linkedinUrl?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  coverPosition: '',
  optimizedCover: '',
  isFullscreen: false,
  twitterUrl: null,
  linkedinUrl: null,
})

const { $modal } = useNuxtApp()

const shouldShowComponent = computed(
  () => props.article?.categories?.length > 0,
)

// Computed properties for frequently used values
const articleTitle = computed(() => props.article?.title || '')
const articleCoverUrl = computed(() => props.article?.coverUrl || '')
const articleCoverCaption = computed(() => props.article?.coverCaption || '')
const articleAuthor = computed(
  () => props.article?.author || { fullName: '', jobTitle: '', company: '' },
)
const hasAuthor = computed(() => Boolean(articleAuthor.value.fullName))
const hasCover = computed(
  () => Boolean(articleCoverUrl.value) && !props.isFullscreen,
)

const getAuthorTitle = () => {
  const values = [
    articleAuthor.value.fullName,
    articleAuthor.value.jobTitle,
  ].filter(Boolean)

  return `By ${values.join(', ')}`
}

const showModalLightbox = () => {
  if (articleCoverUrl.value && !isSizeMobile()) {
    const { $cloudinary } = useNuxtApp()
    const imageLightbox = $cloudinary.resize(articleCoverUrl.value, 'home-hero')
    $modal.open('imageLightbox', imageLightbox)
  }
}
</script>

<template>
  <div
    v-if="shouldShowComponent"
    :class="
      twMerge(
        'mx-auto flex max-w-article-container flex-col',
        isFullscreen ? 'max-w-full mx-0' : '',
      )
    "
  >
    <!-- Cover -->
    <div
      v-if="hasCover"
      class="relative pb-1/2 md:h-[400px] md:pb-0 lg:order-2 lg:h-auto lg:pb-1/2"
    >
      <img
        class="absolute h-full w-full object-cover"
        :class="coverPosition"
        :src="optimizedCover"
        :alt="articleTitle"
        width="350"
        height="600"
      />
      <div
        class="absolute h-full w-full cursor-pointer border-2 border-black/10"
        @click="showModalLightbox"
      />
    </div>

    <!-- Caption image -->
    <figure
      v-if="articleCoverCaption"
      class="wp-caption relative mt-1 px-5 text-[0.65rem] italic text-s-700 md:mt-2 md:text-sm lg:order-3 lg:px-0"
      v-html="articleCoverCaption"
    >
    </figure>

    <!-- Author info -->
    <div
      v-if="hasAuthor"
      class="mt-[10px] hidden px-5 text-sm font-bold text-s-600 lg:order-4 lg:block lg:px-0 lg:text-base"
    >
      <div>
        <p>
          {{ getAuthorTitle() }}
        </p>
        <p>{{ articleAuthor.company }}</p>
      </div>
    </div>

    <!-- Title -->
    <h1
      data-testid="article-title"
      :class="
        twMerge([
          'mx-auto max-w-article-container px-5 text-xl font-bold md:text-4xl lg:order-1 lg:mb-7 lg:mt-0 lg:px-0',
          articleCoverCaption ? 'mt-1' : 'mt-4',
          isFullscreen ? 'max-w-full' : '',
        ])
      "
    >
      {{ articleTitle }}
    </h1>

    <!-- Meta (Mobile & Tablet) -->
    <MobileArticleMetaBox
      v-if="!isFullscreen"
      class="mx-auto mt-2 block w-full max-w-article-container px-5 lg:order-3 lg:hidden lg:px-0"
      :categories="article?.categories || []"
      :published-date="article?.publishedDate || ''"
      :duration="article?.duration || 0"
      :twitter-link="twitterUrl"
      :linked-in-link="linkedinUrl"
      :author="articleAuthor"
    />
  </div>
</template>
